// One production order, opened from the Production screen's order list.
//
// Two jobs: show what the order is actually made of, and take the figures
// back off the sheet once the batch is done.
//
// WHY IT IS AN OVERLAY AND NOT A PAGE
// -----------------------------------
// Somebody clicks into an order from a list of orders and expects to come
// back to that list. A full page navigation loses their filter, their scroll
// position, and the thread of what they were doing.
//
// THE WARNING ABOUT UNISSUED MATERIALS IS NOT DECORATION
// ------------------------------------------------------
// Orders created by the older production module never had their materials
// deducted. Recording output against one puts finished goods on the shelf
// with nothing consumed to make them, and the books stop balancing. The
// server says so, and this screen repeats it rather than swallowing it.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      d = j.detail?.message || j.detail?.error || j.detail || d;
    } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm, fontSize: 13.5,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
};

const num = (v, dp = 3) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

const money = (v) =>
  v === null || v === undefined ? '—'
    : '₦' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2 });

function th(align = 'left') {
  return {
    padding: space(1), textAlign: align, fontWeight: 600,
    borderBottom: `1px solid ${color.border}`, whiteSpace: 'nowrap',
  };
}
function td(align = 'left') {
  return {
    padding: space(1), textAlign: align,
    borderBottom: `1px solid ${color.border}`,
  };
}

async function download(url, fallbackName, notify) {
  try {
    notify?.('Building the file...', 'info');
    const res = await authedFetch(url);
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || `Server returned ${res.status}`);
    }
    const disp = res.headers.get('Content-Disposition') || '';
    const m = disp.match(/filename="?([^"]+)"?/);
    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = m ? m[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
    notify?.(`Downloaded ${a.download}`, 'success');
  } catch (e) { notify?.(`Download failed: ${e.message}`, 'error'); }
}

// ---------------------------------------------------------------------------

// Actual raw material usage, typed back off the printed sheet.
//
// WHY IT IS NOT DERIVED
// ---------------------
// The recipe says 159.309 kg of paraffin. The operator poured what the scale
// said. Only one of those is a fact about this batch, and the system cannot
// work it out -- somebody carries the sheet back and types it in.
//
// Pre-filled with what was issued, because most lines match and the ones that
// do not are the only ones worth anybody's attention. Stock moves by the
// DIFFERENCE between what is typed and what has already left the store, so
// nothing is deducted twice.
function MaterialsForm({ data, orderId, onDone, onCancel, notify }) {
  const materials = data.materials || [];
  // Nothing issued yet means this form IS the deduction. Saying so matters:
  // a button that quietly empties a store is one nobody can predict.
  const firstIssue = materials.length > 0
    && materials.every((m) => Number(m.deducted_quantity || 0) === 0);
  const [values, setValues] = useState(() => {
    const seed = {};
    materials.forEach((m) => {
      seed[m.raw_material_id] = String(
        m.quantity_consumed != null ? m.quantity_consumed
          : (m.deducted_quantity != null && Number(m.deducted_quantity) > 0
            ? m.deducted_quantity : m.quantity_required));
    });
    return seed;
  });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const actuals = {};
    for (const [k, v] of Object.entries(values)) {
      const n = Number(v);
      if (v === '' || Number.isNaN(n) || n < 0) {
        notify?.('Every quantity must be a number of zero or more', 'error');
        return;
      }
      actuals[k] = n;
    }
    setBusy(true);
    try {
      const r = await req(`/api/bom/production-runs/${orderId}/actuals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actuals, note: note || null }),
      });
      notify?.(r.detail, r.approved_now ? 'warning' : 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {firstIssue ? (
        <Banner tone="warning" title="This is the step that deducts the raw materials">
          Nothing has left the store for this order yet, so saving here takes
          the quantities below out of stock and marks the order approved.
          Enter what actually went into the batch, not what the recipe says.
        </Banner>
      ) : (
        <Banner tone="info" title="Enter what was actually used">
          Stock moves by the <strong>difference</strong> between what you type
          and what has already been issued &mdash; more comes out of the store,
          less goes back in. Nothing is deducted twice, so this is safe to
          correct.
        </Banner>
      )}

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              <th style={th()}>Material</th>
              <th style={th()}>Unit</th>
              <th style={th('right')}>Recipe says</th>
              <th style={th('right')}>{firstIssue ? 'Issued so far' : 'Already issued'}</th>
              <th style={th('right')}>Actually used</th>
              <th style={th('right')}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m) => {
              const actual = Number(values[m.raw_material_id] ?? 0);
              const variance = actual - Number(m.quantity_required || 0);
              const off = Math.abs(variance) > 1e-9;
              return (
                <tr key={m.raw_material_id}>
                  <td style={td()}>{m.material}</td>
                  <td style={{ ...td(), color: color.textSecondary }}>{m.unit || '—'}</td>
                  <td style={td('right')}>{num(m.quantity_required)}</td>
                  <td style={{ ...td('right'), color: color.textSecondary }}>
                    {num(m.deducted_quantity)}
                  </td>
                  <td style={{ ...td('right'), width: 140 }}>
                    <input
                      style={{ ...inputStyle, textAlign: 'right' }}
                      inputMode="decimal"
                      value={values[m.raw_material_id] ?? ''}
                      onChange={(e) => setValues({
                        ...values, [m.raw_material_id]: e.target.value })}
                    />
                  </td>
                  <td style={{
                    ...td('right'),
                    color: off ? (variance > 0 ? color.danger : color.warning)
                      : color.textMuted,
                    fontWeight: off ? 600 : 400,
                  }}>
                    {off ? (variance > 0 ? '+' : '') + num(variance) : '—'}
                  </td>
                </tr>
              );
            })}
            {materials.length === 0 && (
              <tr><td style={td()} colSpan={6}>
                This order has no components recorded against it.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: space(2) }}>
        <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
          Note (why the figures differ, if they do)
        </div>
        <input style={inputStyle} value={note}
               onChange={(e) => setNote(e.target.value)}
               placeholder="e.g. spillage during transfer" />
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy || materials.length === 0}>
          {firstIssue
            ? 'Record usage & deduct from stock'
            : 'Record materials used'}
        </Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>
          Back to details
        </Btn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function OutputForm({ data, orderId, onDone, onCancel, notify }) {
  const planned = Number(data.order.quantity_planned || 0);
  const [good, setGood] = useState('');
  const [damaged, setDamaged] = useState('0');
  const [reason, setReason] = useState('');
  const [batchNo, setBatchNo] = useState('');
  // Defaulted to today because most runs are written up the same day, but it
  // is an input, not an assumption: a run written up the next morning was not
  // manufactured the next morning, and the date travels onto the invoice.
  const [madeOn, setMadeOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  // Orders from the older module carry no batch. One is generated if this is
  // left blank, because finished goods with no batch cannot be recalled.
  const needsBatch = !data.order.batch_number;

  const g = Number(good || 0);
  const d = Number(damaged || 0);
  const made = g + d;
  const efficiency = planned > 0 ? (g / planned) * 100 : null;
  const loss = made > 0 ? (d / made) * 100 : null;

  // Nothing issued means nothing was consumed to make these goods.
  const untouched = (data.materials || []).every(
    (m) => Number(m.deducted_quantity || 0) === 0);

  const save = async () => {
    if (good === '' || Number.isNaN(g) || g < 0) {
      notify?.('Enter the good output', 'error'); return;
    }
    if (d > 0 && reason.trim().length < 3) {
      notify?.('Damaged output needs a reason', 'error'); return;
    }
    if (!madeOn || !expiry) {
      notify?.('The manufacturing and expiry dates are both needed', 'error');
      return;
    }
    if (expiry <= madeOn) {
      notify?.('The expiry date has to be after the manufacturing date', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await req(`/api/bom/production-orders/${orderId}/output`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity_good: g,
          quantity_damaged: d,
          damage_reason: d > 0 ? reason.trim() : null,
          batch_number: needsBatch && batchNo.trim() ? batchNo.trim() : null,
          manufactured_on: madeOn,
          expiry_date: expiry,
        }),
      });
      notify?.(r.detail, 'success');
      if (r.warning) notify?.(r.warning, 'warning');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {untouched && (
        <div style={{ marginBottom: space(1.5) }}>
          <Banner tone="warning" title="No materials have been issued for this order">
            Recording output will put finished goods on the shelf with nothing
            consumed to make them, and raw material stock will not be reduced.
            If the materials were taken from the store outside the system, that
            is fine &mdash; but the raw material figures will need correcting
            separately.
          </Banner>
        </div>
      )}

      <Banner tone="info" title="Good units go to stock, damaged units do not">
        Good output enters stock against batch{' '}
        <strong>{data.order.batch_number || (batchNo.trim() || 'a number generated now')}</strong>,
        so it can be traced in a recall. Damaged output is written off and never
        becomes sellable. The dates below go onto the customer&rsquo;s invoice
        and are read back off the cartons before the goods leave.
      </Banner>

      {needsBatch && (
        <div style={{ marginTop: space(1.5) }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Batch number <span style={{ color: color.textMuted }}>
              (leave blank and one will be generated)
            </span>
          </div>
          <input style={inputStyle} value={batchNo}
                 onChange={(e) => setBatchNo(e.target.value)}
                 placeholder="e.g. HWG-CTN-261010-01" />
        </div>
      )}

      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Manufactured on
          </div>
          <input style={inputStyle} type="date" value={madeOn}
                 max={new Date().toISOString().slice(0, 10)}
                 onChange={(e) => setMadeOn(e.target.value)} />
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Expires on
          </div>
          <input style={inputStyle} type="date" value={expiry}
                 min={madeOn}
                 onChange={(e) => setExpiry(e.target.value)} />
        </label>
      </div>

      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Good output</div>
          <input style={inputStyle} value={good} inputMode="decimal"
                 onChange={(e) => setGood(e.target.value)} autoFocus />
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Damaged output</div>
          <input style={inputStyle} value={damaged} inputMode="decimal"
                 onChange={(e) => setDamaged(e.target.value)} />
        </label>
        <label style={{ gridColumn: 'span 2', minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Reason for damage {d > 0 && <span style={{ color: color.danger }}>(required)</span>}
          </div>
          <input style={inputStyle} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. tubes split during sealing" />
        </label>
      </div>

      <div style={{
        marginTop: space(2), display: 'flex', gap: space(3), flexWrap: 'wrap',
        background: color.bg, padding: space(1.5), borderRadius: radius.md,
      }}>
        {[
          ['Yield efficiency', efficiency === null ? '—' : `${efficiency.toFixed(1)}%`, 'good ÷ planned', null],
          ['Loss', loss === null ? '—' : `${loss.toFixed(1)}%`, 'damaged ÷ made', loss > 0 ? color.danger : null],
          ['Shortfall', num(planned - made, 0), 'planned − made', null],
        ].map(([label, value, hint, tone]) => (
          <div key={label}>
            <div style={{ fontSize: 12, color: color.textSecondary }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 650, color: tone || color.text }}>{value}</div>
            <div style={{ fontSize: 11.5, color: color.textMuted }}>{hint}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy}>Record output</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Back to details</Btn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function ProductionOrderPanel({ orderId, onClose, notify }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('details');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try { setData(await req(`/api/bom/production-runs/${orderId}`)); }
    catch (e) { setErr(e.message); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  // Escape closes, because an overlay that traps you is worse than no overlay.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const approve = async () => {
    setBusy(true);
    try {
      let reason = null;
      try {
        const r = await req(`/api/bom/production-orders/${orderId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: null }),
        });
        notify?.(r.detail, r.approved_with_shortage ? 'warning' : 'success');
        await load();
        return;
      } catch (e) {
        if (!/reason is required/i.test(e.message)) throw e;
        reason = window.prompt(
          `${e.message}\n\nWhy is this being approved short? (at least 5 characters)`, '');
        if (reason === null) { notify?.('Approval cancelled', 'info'); return; }
        if (reason.trim().length < 5) {
          notify?.('A reason of at least 5 characters is required', 'error'); return;
        }
      }
      const r = await req(`/api/bom/production-orders/${orderId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notify?.(r.detail, 'warning');
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const order = data?.order;
  const status = (order?.status || '').toLowerCase();
  // The backend accepts both. Only offering 'draft' meant every order the
  // older module created -- which is all of them -- had no Approve button at
  // all, and no way to issue its materials.
  const canApprove = ['draft', 'planned'].includes(status)
    && !order?.approved_at;
  const canRecord = ['planned', 'approved', 'in_progress'].includes(status)
    && !order?.output_recorded_at;

  const body = () => {
    if (err) return <ErrorBox msg={err} />;
    if (!data) return <SkeletonCards n={2} />;

    if (mode === 'output') {
      return (
        <OutputForm
          data={data} orderId={orderId} notify={notify}
          onCancel={() => setMode('details')}
          onDone={() => { setMode('details'); load(); }}
        />
      );
    }

    if (mode === 'materials') {
      return (
        <MaterialsForm
          data={data} orderId={orderId} notify={notify}
          onCancel={() => setMode('details')}
          onDone={() => { setMode('details'); load(); }}
        />
      );
    }

    const materials = data.materials || [];
    const totalCost = materials.reduce(
      (t, m) => t + Number(m.quantity_required || 0) * Number(m.unit_cost || 0), 0);

    return (
      <div>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap', marginBottom: space(1) }}>
          <Chip tone={status === 'completed' ? 'success'
            : status === 'approved' ? 'info'
              : status === 'cancelled' ? 'danger' : 'neutral'}>
            {order.status}
          </Chip>
          {order.batch_number && <Chip tone="neutral">Batch {order.batch_number}</Chip>}
          {order.approved_with_shortage && <Chip tone="warning">Approved short</Chip>}
        </div>

        <div style={{ fontSize: 13, color: color.textSecondary, marginBottom: space(1.5) }}>
          {order.product_name} · planned {num(order.quantity_planned, 0)}
          {order.warehouse_name ? ` · ${order.warehouse_name}` : ''}
          {order.approved_by_name ? ` · approved by ${order.approved_by_name}` : ''}
          {order.expiry_date ? ` · expires ${String(order.expiry_date).slice(0, 10)}` : ''}
        </div>

        {order.approval_reason && (
          <div style={{
            marginBottom: space(1.5), fontSize: 13,
            background: `${color.warning}14`, borderLeft: `3px solid ${color.warning}`,
            padding: `${space(1)} ${space(1.5)}`, borderRadius: radius.sm,
          }}>
            Approved short: {order.approval_reason}
          </div>
        )}

        {materials.length > 0
          && materials.every((m) => Number(m.deducted_quantity || 0) === 0)
          && !order.output_recorded_at && (
          <div style={{ marginBottom: space(1.5) }}>
            <Banner tone="warning" title="No raw materials have been deducted yet">
              Two ways to do this, and either is fine. <strong>Approve &amp;
              issue materials</strong> takes the recipe quantities out of the
              store now, and you reconcile afterwards. <strong>Record materials
              used</strong> takes out exactly what you enter, which suits a
              batch that has already been run.
            </Banner>
          </div>
        )}

        <div style={{ fontWeight: 650, fontSize: 14, marginBottom: space(1) }}>
          Components ({materials.length})
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: color.bg }}>
                <th style={th()}>Material</th>
                <th style={th()}>Unit</th>
                <th style={th('right')}>Per unit</th>
                <th style={th('right')}>Required</th>
                <th style={th('right')}>Issued</th>
                <th style={th('right')}>Still owed</th>
                <th style={th('right')}>Actually used</th>
                <th style={th('right')}>Unit cost</th>
                <th style={th('right')}>Line cost</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m) => {
                const owed = Number(m.still_owed || 0);
                const lineCost = Number(m.quantity_required || 0) * Number(m.unit_cost || 0);
                return (
                  <tr key={m.raw_material_id}>
                    <td style={td()}>{m.material}</td>
                    <td style={{ ...td(), color: color.textSecondary }}>{m.unit || '—'}</td>
                    <td style={td('right')}>{num(m.qty_per_unit, 6)}</td>
                    <td style={{ ...td('right'), fontWeight: 600 }}>{num(m.quantity_required)}</td>
                    <td style={td('right')}>{num(m.deducted_quantity)}</td>
                    <td style={{
                      ...td('right'),
                      color: owed > 0 ? color.danger : color.textMuted,
                      fontWeight: owed > 0 ? 600 : 400,
                    }}>{owed > 0 ? num(owed) : '—'}</td>
                    <td style={td('right')}>{num(m.quantity_consumed)}</td>
                    <td style={td('right')}>{money(m.unit_cost)}</td>
                    <td style={td('right')}>{m.unit_cost ? money(lineCost) : '—'}</td>
                  </tr>
                );
              })}
              {materials.length === 0 && (
                <tr><td style={td()} colSpan={9}>
                  No components recorded against this order.
                </td></tr>
              )}
            </tbody>
            {totalCost > 0 && (
              <tfoot>
                <tr>
                  <td style={{ ...td('right'), fontWeight: 650 }} colSpan={8}>
                    Total material cost
                  </td>
                  <td style={{ ...td('right'), fontWeight: 650 }}>{money(totalCost)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {order.output_recorded_at && (
          <div style={{
            marginTop: space(2), display: 'flex', gap: space(3), flexWrap: 'wrap',
            background: color.bg, padding: space(1.5), borderRadius: radius.md,
          }}>
            {(() => {
              const planned = Number(order.quantity_planned || 0);
              const g = Number(order.quantity_good || 0);
              const d = Number(order.quantity_damaged || 0);
              const made = g + d;
              const eff = planned > 0 ? (g / planned) * 100 : null;
              const loss = made > 0 ? (d / made) * 100 : null;
              return [
                ['Good', num(g, 0), null],
                ['Damaged', num(d, 0), d > 0 ? color.danger : null],
                ['Yield efficiency', eff === null ? '—' : `${eff.toFixed(1)}%`,
                  eff !== null && eff < 90 ? color.warning : color.success],
                ['Loss', loss === null ? '—' : `${loss.toFixed(1)}%`,
                  loss > 0 ? color.danger : null],
                ['Shortfall', num(planned - made, 0), null],
              ].map(([label, value, tone]) => (
                <div key={label}>
                  <div style={{ fontSize: 12, color: color.textSecondary }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 650, color: tone || color.text }}>{value}</div>
                </div>
              ));
            })()}
          </div>
        )}

        {order.damage_reason && (
          <div style={{
            marginTop: space(1.5), fontSize: 13,
            background: `${color.danger}10`, borderLeft: `3px solid ${color.danger}`,
            padding: `${space(1)} ${space(1.5)}`, borderRadius: radius.sm,
          }}>
            Damage: {order.damage_reason}
          </div>
        )}

        {(data.events || []).length > 0 && (
          <details style={{ marginTop: space(2) }}>
            <summary style={{ cursor: 'pointer', fontSize: 12.5, color: color.textSecondary }}>
              History ({data.events.length})
            </summary>
            <div style={{ marginTop: space(1) }}>
              {data.events.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: color.textSecondary, padding: '3px 0' }}>
                  <strong>{e.event}</strong>
                  {e.actor_name ? ` by ${e.actor_name}` : ''}
                  {e.detail ? ` — ${e.detail}` : ''}
                  {e.reason ? ` (${e.reason})` : ''}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: space(3), overflowY: 'auto',
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
           style={{ width: '100%', maxWidth: 1100 }}>
        <Card>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: space(1), flexWrap: 'wrap',
            marginBottom: space(1),
          }}>
            <div style={{ fontWeight: 650, fontSize: 16 }}>
              {order?.order_number || 'Production order'}
              {mode === 'output' && ' — record output'}
            </div>
            <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
              {mode === 'details' && data && (
                <>
                  <Btn size="sm" variant="secondary"
                       onClick={() => download(
                         `/api/bom/production-orders/${orderId}/requirements/download?format=pdf`,
                         `${order?.order_number || 'order'}-requirements.pdf`, notify)}>
                    Sheet (PDF)
                  </Btn>
                  <Btn size="sm" variant="secondary"
                       onClick={() => download(
                         `/api/bom/production-orders/${orderId}/requirements/download?format=csv`,
                         `${order?.order_number || 'order'}-requirements.csv`, notify)}>
                    CSV
                  </Btn>
                  {canApprove && (
                    <Btn size="sm" onClick={approve} disabled={busy}>
                      Approve &amp; issue materials
                    </Btn>
                  )}
                  {(data.materials || []).length > 0 && (
                    <Btn size="sm" variant="secondary"
                         onClick={() => setMode('materials')}>
                      Record materials used
                    </Btn>
                  )}
                  {canRecord && (
                    <Btn size="sm" onClick={() => setMode('output')}>
                      Record output
                    </Btn>
                  )}
                </>
              )}
              <Btn size="sm" variant="secondary" onClick={onClose}>Close</Btn>
            </div>
          </div>
          {body()}
        </Card>
      </div>
    </div>
  );
}
